/**
 * useCompTabSources — what the Events / Competition tab used to hold in its own
 * state, one level up so a tab switch loses nothing
 * (docs/spec/01f-tab-loading.md, "useCompTabSources").
 *
 * The tab is unmounted on every switch to another tab. Three things it held
 * were lost and read again on each return:
 *
 *   - the open cards' details (groups, runs, run occupancy) — a store here,
 *     filled by `useCompCardDetails` (60 s freshness, see that file);
 *   - the viewer's own participation document per active challenge — a kept
 *     listener set;
 *   - the friends' competitor registrations per live competition — a kept
 *     listener set.
 *
 * The two listener sets follow the `useKeptListener` rules: subscribed only
 * while the tab holds them open (`open()`), the last value kept after the
 * release and shown at once on return, dropped when the set they describe
 * changes (their key), cleared on a gym switch.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { doc, collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { chunkForInQuery } from "../utils";
import { useKeptListener } from "./useRangeCache";
import { useCompCardDetailsStore } from "./useCompCardDetails";

// Stable empty values: a render for a scope without a kept value must not hand
// the tab a fresh `{}` every time.
const EMPTY_MAP = Object.freeze({});
const NO_COMPETITIONS = Object.freeze([]);
const NO_FRIENDS = Object.freeze([]);

// A challenge the climber can still join or leave. Named, not an inline
// `status === "active"` lookup: that shape is what `activeCompetition.test.js`
// polices for the MAP's active competition, a different question.
const isLiveChallenge = (c) => c.type === "challenge" && c.status === "active";

/**
 * @param {object} p
 * @param {string|null} p.gymId
 * @param {string|null} p.uid           the signed-in viewer
 * @param {object[]}    p.competitions  the gym's competition docs (the always-on list)
 * @param {string[]}    p.friendUids    accepted friends' uids
 * @returns {{
 *   cardDetails: object,                    store for `useCompCardDetails`
 *   myChallengeParticipation: Object,       compId -> participants/{uid} data | null
 *   friendRegsByComp: Object,               compId -> friends' competitor registrations
 *   open: Function,                         () => release; the tab calls it in an effect
 *   setLiveCompIdsKey: Function,            the tab states which competitions are live
 * }}
 */
export function useCompTabSources({ gymId, uid, competitions = NO_COMPETITIONS, friendUids = NO_FRIENDS }) {
  const cardDetails = useCompCardDetailsStore(gymId);

  // ── Own challenge participation ───────────────────────────────────────────
  // One tiny doc listener per ACTIVE challenge, so the card's join / leave
  // switch reflects the live state. Derived from the competition list alone:
  // an active challenge is visible to everyone, no tab-local rule is involved.
  const activeChallengeIdsKey = useMemo(
    () => competitions.filter(isLiveChallenge).map(c => c.id).sort().join(","),
    [competitions],
  );
  const participationKey = uid && activeChallengeIdsKey ? `${uid}::${activeChallengeIdsKey}` : null;
  const participationSeed = useRef({ key: null, data: EMPTY_MAP });
  const subscribeParticipation = (onNext) => {
    const ids = activeChallengeIdsKey.split(",").filter(Boolean);
    // Start from the kept value of THIS (gym, key), so the first snapshot of one
    // challenge does not blank the switches of the others.
    const data = participationSeed.current.key === `${gymId}|${participationKey}` ? { ...participationSeed.current.data } : {};
    const emit = () => {
      const next = { ...data };
      participationSeed.current = { key: `${gymId}|${participationKey}`, data: next };
      onNext(next);
    };
    const unsubs = ids.map(id => onSnapshot(
      doc(db, "gyms", gymId, "competitions", id, "participants", uid),
      snap => { data[id] = snap.exists() ? snap.data() : null; emit(); },
      // A failed listener leaves its entry as it was; the others carry on.
      () => emit(),
    ));
    return () => unsubs.forEach(u => u());
  };
  const [myChallengeParticipation, participationApi] = useKeptListener(gymId, {
    label: "myChallengeParticipation",
    key: participationKey,
    subscribe: subscribeParticipation,
    initial: EMPTY_MAP,
  });

  // ── Friends' registrations ────────────────────────────────────────────────
  // Lets the card and the registration views show "which group your friends
  // registered in" without loading every participant's record: `uid in <chunk>`
  // (≤ 30) per (competition × chunk) pair. Which competitions are live
  // (active or a draft the viewer may see, external ones included) depends on
  // the viewer's permissions and registrations, which only the tab resolves —
  // it states the ids through `setLiveCompIdsKey`, and the value is kept here
  // after the tab is gone.
  const [liveCompIdsKey, setLiveCompIdsKey] = useState("");
  const friendUidsKey = useMemo(() => friendUids.slice().sort().join(","), [friendUids]);
  const friendsKey = uid && friendUidsKey && liveCompIdsKey ? `${uid}::${friendUidsKey}::${liveCompIdsKey}` : null;
  const friendsSeed = useRef({ key: null, data: EMPTY_MAP });
  const subscribeFriends = (onNext) => {
    const friends = friendUidsKey.split(",");
    const compIds = liveCompIdsKey.split(",");
    const result = friendsSeed.current.key === `${gymId}|${friendsKey}` ? { ...friendsSeed.current.data } : {};
    // chunkData[compId][chunkIdx] = registrations[]
    const chunkData = {};
    const unsubs = [];
    const emit = () => {
      const next = { ...result };
      friendsSeed.current = { key: `${gymId}|${friendsKey}`, data: next };
      onNext(next);
    };
    compIds.forEach(compId => {
      chunkData[compId] = {};
      chunkForInQuery(friends).forEach((batch, chunkIdx) => {
        const q = query(
          collection(db, "gyms", gymId, "competitions", compId, "registrations"),
          where("uid", "in", batch),
          where("role", "==", "competitor"),
        );
        unsubs.push(onSnapshot(q, snap => {
          chunkData[compId][chunkIdx] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          result[compId] = Object.values(chunkData[compId]).flat();
          emit();
        }, () => emit()));
      });
    });
    return () => unsubs.forEach(u => u());
  };
  const [friendRegsByComp, friendsApi] = useKeptListener(gymId, {
    label: "friendRegsByComp",
    key: friendsKey,
    subscribe: subscribeFriends,
    initial: EMPTY_MAP,
  });

  // One open() for both sets: the tab holds them while it is rendered.
  const openParticipation = participationApi.open;
  const openFriends = friendsApi.open;
  const open = useCallback(() => {
    const releaseParticipation = openParticipation();
    const releaseFriends = openFriends();
    return () => { releaseParticipation(); releaseFriends(); };
  }, [openParticipation, openFriends]);

  return useMemo(() => ({
    cardDetails,
    myChallengeParticipation,
    friendRegsByComp,
    open,
    setLiveCompIdsKey,
  }), [cardDetails, myChallengeParticipation, friendRegsByComp, open]);
}
